'use client';
import React, { useEffect, useState } from 'react';
import Typography from '@/components/shared/typography/typography.component';
import { GreenInfo } from '../../../../public/Images';
import SylndrButton from '../Button/button-styled.component';
import Link from '@/components/WrappedLink';
import { useAnalytics } from '@/integrations';
import { useTranslation, useAppDispatch } from '@/hook';
import {
    DisplayedCarInfoInterface,
    ExperimentShowFinancingButton,
    isReserveHomeAllowed,
    isReserveHubAllowed,
    isSeeDealer0Allowed,
    isSeeHomeAllowed,
    isSeeHubAllowed,
} from '@/types';
import Image from 'next/image';
import { NEXT_APP_MAINTENANCE_DISABLE_BOOKING } from '@/constants';
import { toggleLoading } from '@/redux/reducers/checkoutReducer';
import { DownloadApp } from '@/components/download-app';
import { useFeatureValue } from '@growthbook/growthbook-react';

const ReserveSeeNotify = ({
    carInfo,
    lang,
    setNotifyModal,
    seeCarButtonClicked,
    showTip,
}: {
    carInfo: DisplayedCarInfoInterface;
    lang: string;
    setNotifyModal: React.Dispatch<React.SetStateAction<boolean>>;
    seeCarButtonClicked: () => void;
    showTip: boolean;
}) => {
    const { trackEvent } = useAnalytics();
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();

    const FEATURE_FLAG_SHOW_FINANCING_BUTTON = 'show-financing-button';

    const showFinancingButtonExperiment = useFeatureValue(
        FEATURE_FLAG_SHOW_FINANCING_BUTTON,
        ExperimentShowFinancingButton.ORIGINAL
    );
    const [showFinancingButton, setShowFinancingButton] = useState<boolean>();

    useEffect(() => {
        if (showFinancingButtonExperiment) {
            setShowFinancingButton(
                showFinancingButtonExperiment !==
                    ExperimentShowFinancingButton.HIDE
            );
        }
    }, [showFinancingButtonExperiment]);

    return (
        carInfo && (
            <>
                <div
                    className={'flex items-center gap-4 h-[50px] md:h-[60px]  '}
                >
                    {carInfo.isReserved ? (
                        <button
                            className={
                                'w-full border-gray-300 border-solid bg-white rounded-lg border outline-0 h-[50px] md:h-[60px] md:reserve-see-lg:h-[60px]'
                            }
                            onClick={() => setNotifyModal(true)}
                        >
                            <Typography
                                variant="body1SemiBold"
                                className={'text-black'}
                            >
                                {t('Notify Me')}
                            </Typography>
                        </button>
                    ) : carInfo?.bookingType &
                          (isReserveHubAllowed(carInfo?.bookingType) ||
                              isReserveHomeAllowed(carInfo?.bookingType)) &&
                      carInfo.id ? (
                        <Link
                            href={`/${lang}/car-details/${carInfo.id}/checkout/reserve`}
                            className={
                                'no-underline w-full h-full cursor-pointer'
                            }
                        >
                            <SylndrButton
                                onClick={() => {
                                    trackEvent('Action: Reserve Car', {
                                        carName: carInfo.carName,
                                        isRefurbished: carInfo.isRefurbished,
                                        newlyAdded:
                                            carInfo.isNewlyAdded &&
                                            !(
                                                carInfo.isReserved ||
                                                carInfo.isPurchaseInProgress
                                            ),
                                        status: carInfo.isPurchaseInProgress
                                            ? 'Being Sold'
                                            : carInfo.isReserved
                                            ? 'Reserved'
                                            : 'Not Reserved',
                                    });
                                    dispatch(toggleLoading(false));
                                }}
                                variant={'primary'}
                                disabled={
                                    carInfo.isReserved ||
                                    NEXT_APP_MAINTENANCE_DISABLE_BOOKING
                                }
                            >
                                <Typography variant="body1SemiBold">
                                    {t('reserve car')}
                                </Typography>
                            </SylndrButton>
                        </Link>
                    ) : (
                        <></>
                    )}

                    {carInfo?.bookingType &
                        (isSeeHomeAllowed(carInfo?.bookingType) ||
                            isSeeHubAllowed(carInfo?.bookingType)||
                            isSeeDealer0Allowed(carInfo?.bookingType)) &&
                    carInfo.id ? (
                        <Link
                            href={`/${i18n.language}/car-details/${carInfo.id}/checkout/see`}
                            className={
                                'no-underline w-full h-full cursor-pointer'
                            }
                        >
                            <SylndrButton
                                onClick={() => {
                                    trackEvent('Action: Book Visit', {
                                        carName: carInfo.carName,
                                        isRefurbished: carInfo.isRefurbished,
                                        newlyAdded:
                                            carInfo.isNewlyAdded &&
                                            !(
                                                carInfo.isReserved ||
                                                carInfo.isPurchaseInProgress
                                            ),
                                        status: carInfo.isPurchaseInProgress
                                            ? 'Being Sold'
                                            : carInfo.isReserved
                                            ? 'Reserved'
                                            : 'Not Reserved',
                                    });
                                    seeCarButtonClicked();
                                    dispatch(toggleLoading(false));
                                }}
                                disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                                variant={
                                    !(
                                        carInfo.bookingType &
                                        (isReserveHubAllowed(
                                            carInfo?.bookingType
                                        ) ||
                                            isReserveHomeAllowed(
                                                carInfo?.bookingType
                                            ))
                                    ) || carInfo.isReserved
                                        ? 'primary'
                                        : 'secondary'
                                }
                            >
                                <Typography variant="body1SemiBold">
                                    {t('See Car')}
                                </Typography>
                            </SylndrButton>
                        </Link>
                    ) : (
                        <></>
                    )}

                    {showFinancingButton &&
                        !carInfo.isReserved &&
                        !carInfo.isSold &&
                        !carInfo.isPurchaseInProgress &&
                        (((isSeeHomeAllowed(carInfo?.bookingType) ||
                            isSeeHubAllowed(carInfo?.bookingType) ||
                                    isSeeDealer0Allowed(carInfo?.bookingType)) &&
                            !(
                                isReserveHubAllowed(carInfo?.bookingType) ||
                                isReserveHomeAllowed(carInfo?.bookingType)
                            )) ||
                            (!(
                                isSeeHomeAllowed(carInfo?.bookingType) ||
                                isSeeHubAllowed(carInfo?.bookingType)||
                                isSeeDealer0Allowed(carInfo?.bookingType)
                            ) &&
                                (isReserveHubAllowed(carInfo?.bookingType) ||
                                    isReserveHomeAllowed(
                                        carInfo?.bookingType
                                    )))) && (
                            <div className="w-full h-full">
                                <DownloadApp
                                    text={t('financing')}
                                    deepLink={`CarProfileScreen/${carInfo.id}`}
                                    source="Main Financing CTA in Car Profile"
                                    textVariant="body2SemiBold"
                                    buttonProps={{
                                        variant: 'swift',
                                    }}
                                />
                            </div>
                        )}
                </div>
                {!carInfo.isPurchaseInProgress &&
                carInfo.isReserved &&
                showTip ? (
                    <div
                        className={
                            'flex p-2.5 bg-success-50 rounded-lg gap-1.5 items-start content-center justify-center w-full'
                        }
                    >
                        <Image
                            src={GreenInfo}
                            alt="GreenInfo"
                            width={20}
                            height={20}
                        />
                        <Typography
                            variant="captionMedium"
                            className={'text-success-600'}
                        >
                            {t(
                                'Clicking on Notify Me will send you a message once the the car is open for reservation again'
                            )}
                        </Typography>
                    </div>
                ) : null}
            </>
        )
    );
};

export default ReserveSeeNotify;
